import React, { useState, useEffect, useRef, useMemo } from "react";
import { Container, Button, Progress, Row, Col, Card, CardBody, Form, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  useParams,
} from "react-router-dom";
import styles from "../../Library/AdverseEvent/css/adverseEvent.module.scss";
import tabStyles from "../../PrintLabel/MainPrintListTabs/css/labeltypetabs.module.scss";
import ac from "../css/scanning.module.scss";
import resStyles from "../css/scanningSessionResponsive.module.scss";
import SubjectToVacutainer from "../components/SubjectToVacutainerScanning";
import VacutainerToAliquots from "../components/VacutainerToAliquotsScanning";
import MobileHeader from "../components/MobileScanningHeader";
import MobileWorkflowSelector from "../components/MobileScanningWorkflowSelector";
import MobileScansOverlay from "../components/MobileScanningScansOverlay";
import MobileStep1Scanner from "../components/MobileScanningStep1Scanner";
import MobileStep2Scanner from "../components/MobileScanningStep2Scanner";
import { useSampleCollectionTimeService } from "modules/ScanningApp/hooks/useSampleCollectionTime";
import {
  useScanningSessionService,
  useScanningSessionProgressService,
  useScanningScanSampleCollectionService,
  useScanningScanSampleSeperationService,
} from "../api/useScanningSession";
import {
  CurrentUser,
  GetUserData,
  showToastMessage,
  GetSystemSettingValue,
} from "common/service/CommonService";
import { ACCESSIONING_PATHS, SCANNING_PATHS } from "Routes/paths";
import { queryClient } from "api/queryClient";
import FullPageLoader from "Components/Common/FullPageLoader";
import ScanningSessionProgressModal from "../components/ScanningSessionProgressModal";
import SkipReasonModal from "../components/SkipReasonModal";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import RouteGuard from "Routes/RouteGuard";
import { usePermission } from "context/PermissionContext";

const playSuccessBeep = async () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "square";
    oscillator.frequency.setValueAtTime(1800, audioContext.currentTime);

    gainNode.gain.setValueAtTime(0.9, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.18);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.18);
  } catch (error) {
    console.error("Beep sound error:", error);
  }
};

const playErrorBeep = async () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "sawtooth";
    oscillator.frequency.setValueAtTime(220, audioContext.currentTime);

    gainNode.gain.setValueAtTime(0.9, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.35);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.35);
  } catch (error) {
    console.error("Beep sound error:", error);
  }
};


type AccessioningTabKey = "select" | "step1" | "step2";

const ScanningSessionPage: React.FC = () => {
  document.title = "Scanning Session | ZENTIXS VISTA";

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const sampleCollectionMode = GetSystemSettingValue("Sample Collection Mode") || GetSystemSettingValue("Collection Capture Mode") || GetSystemSettingValue("Collection Mode") || "Auto";
  const isAutoCollect = sampleCollectionMode.toLowerCase() === "auto";

  // Scan lock and in-flight processing refs
  const scanLockRef = useRef(false);
  const lastScannedRef = useRef("");
  const isProcessingRef = useRef(false);
  const [isProcessing, setIsProcessingState] = useState(false);
  const setProcessing = (val: boolean) => {
    isProcessingRef.current = val;
    setIsProcessingState(val);
  };

  // Responsive Width State
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isDesktop = windowWidth > 1024;

  const isScanning = location.pathname.startsWith("/scanning");
  const sessionPathPattern = isScanning ? SCANNING_PATHS.SESSION : ACCESSIONING_PATHS.SESSION;
  const listPath = isScanning ? SCANNING_PATHS.VIEW : ACCESSIONING_PATHS.LIST;

  // Active Tab & Progress Modal States
  const [activeTab, setActiveTab] = useState<AccessioningTabKey>("select");
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const isManualAction = useRef(false);
  // Performance timer tracking
  const scanStartTimeRef = useRef<number>(performance.now());

  const resetScanStartTime = () => {
    scanStartTimeRef.current = performance.now();
  };

  // const logScanPerformance = (barcode: string) => {
  //   const latency = Math.round(performance.now() - scanStartTimeRef.current);
  //   setLastScanLatency(latency);
  //   setEngineLogs((prev) => [
  //     {
  //       barcode,
  //       engine: "barcode_detector",
  //       latency,
  //       timestamp: new Date().toLocaleTimeString(),
  //     },
  //     ...prev.slice(0, 19),
  //   ]);
  // };

  // Sync tab from URL path
  const { hasPermission } = usePermission()
  const permission1 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER)
  const permission2 = hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT)
  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];

    if (!isDesktop) {
      if (permission1 && last === "step2") {
        setActiveTab("step2");
      } else if (permission2 && last === "step1") {
        setActiveTab("step1");
      } else {
        setActiveTab("select");
      }
    } else {
      if (last === "step2") setActiveTab("step2");
      else setActiveTab("step1");
    }
  }, [location.pathname, isDesktop]);

  const toggleTab = (tabKey: AccessioningTabKey): void => {
    const basePath = sessionPathPattern.replace(":id", id || "");
    if (isDesktop) {
      navigate(tabKey === "step1" ? basePath : `${basePath}/${tabKey}`);
    } else {
      if (tabKey === "select") {
        navigate(basePath);
      } else {
        navigate(`${basePath}/${tabKey}`);
      }
    }
    setActiveTab(tabKey);
  };

  // Session Data & Status Hook
  const user = GetUserData() as CurrentUser;
  const { SelectQuery, UpdateMutation } = useScanningSessionService({
    sessionid: id,
  });

  const { SessionProgressQuery } = useScanningSessionProgressService({
    sessionid: id,
    currentpage: null,
    pagesize: null,
    tabstatus: "ALL",
  });

  const sessionData = SelectQuery?.data?.data?.[0];

  useEffect(() => {
    if (sessionData) {
      const dataToSave = {
        ...sessionData,
        sessionid: sessionData.sessionid || (id ? parseInt(id) : undefined)
      };
      sessionStorage.setItem("scanning_viewing_session", JSON.stringify(dataToSave));
      sessionStorage.setItem("scanning_flow_selected", "existing");
    }
  }, [sessionData, id]);

  const totalSubs = sessionData?.total_subjects ?? sessionData?.totalsubjects ?? 0;
  const totalAliquotsPerSub = sessionData?.total_aliquots;

  // Calculate actual completed and in-progress subjects on client side
  const progressItems = SessionProgressQuery?.data?.data || [];
  const { completedSubsCount, inProgressSubsCount } = useMemo(() => {
    const backendSkipped = sessionData?.skipped_subjects || 0;
    if (progressItems.length === 0) {
      // Fallback mathematical estimate when progressItems is not loaded/empty
      const completedAliquots = sessionData?.completed_aliquots || 0;
      const step1Completed = (sessionData?.completed_subjects || 0) + backendSkipped;
      const cEst = Math.floor(completedAliquots / Math.max(totalAliquotsPerSub || 1, 1)) + backendSkipped;
      const iEst = Math.max(0, step1Completed - cEst);
      return {
        completedSubsCount: cEst,
        inProgressSubsCount: iEst,
      };
    }

    let completed = 0;
    let inProgress = 0;
    progressItems.forEach((item: any) => {
      const overallStatus = item.overall_status?.toUpperCase();
      const vacStatus = item.vaccutainer_status?.toUpperCase();
      const isSkipped = overallStatus === "SKIPPED" || vacStatus === "SKIPPED";

      if (isSkipped) {
        completed++;
      } else {
        const hasVacutainer = vacStatus === "COMPLETED";

        const aliquotsObj = item.aliquots || {};
        const aliquotKeys = Object.keys(aliquotsObj);
        const allAliquotsCompleted = aliquotKeys.length > 0 && aliquotKeys.every(
          (key) => aliquotsObj[key]?.toUpperCase() === "COMPLETED"
        );

        if (hasVacutainer && allAliquotsCompleted) {
          completed++;
        } else if (hasVacutainer) {
          inProgress++;
        }
      }
    });

    return {
      completedSubsCount: completed,
      inProgressSubsCount: inProgress,
    };
  }, [progressItems, sessionData, totalAliquotsPerSub]);

  const completedSubs = completedSubsCount;
  const inProgressSubs = inProgressSubsCount;

  const handleUpdateStatus = (
    status: "paused" | "completed" | "active",
    shouldNavigate = true,
  ) => {
    if (shouldNavigate) isManualAction.current = true;
    const payload = {
      sessionid: id ? parseInt(id) : 1,
      sessionstate: status,
      updatereason: "Update Session Status",
    };

    UpdateMutation.mutate(payload, {
      onError: (err: any) => {
        showToastMessage("error", err || `Failed to ${status} session`);
      },
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
        if (shouldNavigate) {
          showToastMessage(
            "success",
            res?.message || `Session ${status} successfully`,
          );
          if (status === "completed") {
            sessionStorage.removeItem("scanning_viewing_session");
            sessionStorage.removeItem("scanning_flow_selected");
          }
          navigate(listPath, { replace: true, state: { viewingSessionId: id } });
        } else {
          showToastMessage(
            "success",
            res?.message || `Session ${status} successfully`,
          );
        }
      },
    });
  };

  const displayStatusRef = useRef(sessionData?.display_status);
  useEffect(() => {
    displayStatusRef.current = sessionData?.display_status;
  }, [sessionData?.display_status]);

  // Auto-pause session when navigating away
  useEffect(() => {
    return () => {
      if (!isManualAction.current && displayStatusRef.current !== "PAUSED") {
        handleUpdateStatus("paused", false);
      }
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 📱 Mobile / Tablet States & Scan Handlers
  // ───────────────────────────────────────────────────────────────────────────
  const [isShowingAllScans, setIsShowingAllScans] = useState(false);

  // Scan states
  const [subjectBarcode, setSubjectBarcode] = useState("");
  const [vacutainerBarcode, setVacutainerBarcode] = useState("");
  const [subjectStatus, setSubjectStatus] = useState<"idle" | "matched" | "mismatch">("idle");
  const [vacutainerStatus, setVacutainerStatus] = useState<"idle" | "matched" | "mismatch">("idle");

  const [showCollectModal, setShowCollectModal] = useState(false);
  const [collectTime, setCollectTime] = useState("");
  const [isCollectScanning, setIsCollectScanning] = useState(false);

  // Step 2 Scan States
  const [activeAliquotIndex, setActiveAliquotIndex] = useState(0); // 0 = vacutainer, 1+ = aliquot index
  const [aliquotStatus, setAliquotStatus] = useState<"idle" | "matched" | "mismatch">("idle");
  const [aliquotBarcodes, setAliquotBarcodes] = useState<string[]>([]);

  const [formMessage, setFormMessage] = useState<{
    text: string;
    type: "success" | "error" | "";
  }>({ text: "", type: "" });

  // APIs for Scans (Collection and Separation)
  const collectionService = useScanningScanSampleCollectionService({
    sessionid: id ? parseInt(id) : sessionData?.sessionid || 0,
    searchtext: "",
    currentpage: 1,
    pagesize: 10,
  });

  const separationService = useScanningScanSampleSeperationService({
    sessionid: id ? parseInt(id) : sessionData?.sessionid || 0,
    searchtext: "",
    currentpage: 1,
    pagesize: 10,
  });

  const { SelectAllQuery: sampleCollectionTimeQuery, UpdateMutation: CollectionTimeUpdateMutation } = useSampleCollectionTimeService({
    studyid: sessionData?.studyid,
    period_number: sessionData?.period_number,
    timepoint_number: sessionData?.timepoint_number,
    filterstatus: "ALL",
  });

  // Invalidate session progress & select queries cleanly when navigating back to workflow selection screen
  useEffect(() => {
    if (id && activeTab === "select") {
      queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
      queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
    }
  }, [activeTab, id]);

  const getNow = (): string => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  const getNowDisplay = (): string => {
    const now = new Date();
    return now.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const handleCollectClick = () => {
    scanLockRef.current = false;
    lastScannedRef.current = "";
    if (isAutoCollect) {
      // AUTO MODE: Open collection scanning window to re-scan vacutainer
      setIsCollectScanning(true);
      setFormMessage({ text: "", type: "" });
    } else {
      // MANUAL MODE: Open Confirm Collection Modal
      setCollectTime(getNowDisplay());
      setShowCollectModal(true);
    }
  };

  const handleConfirmCollect = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    // Insert FIRST — scan record exists even if time-update fails afterward
    collectionService.InsertMutation.mutate(
      {
        sessionid: id ? parseInt(id) : sessionData?.sessionid,
        subjectbarcode: subjectBarcode,
        vaccutainerbarcode: vacutainerBarcode,
        result: "valid",
        resultmessage: "Match & Sample Collected Successfully",
        isverify: false,
        isinsert: true,
      },
      {
        onSuccess: () => {
          const payload = {
            vaccutainerbarcode: vacutainerBarcode,
            samplecollectiontime: getNow(),
            updatedby: Number(user?.userid),
            updatereason: "Sample collection recorded",
          };

          CollectionTimeUpdateMutation.mutate(payload, {
            onSuccess: (res: any) => {
              showToastMessage("success", res?.message || "Sample collection time updated successfully.");
              setShowCollectModal(false);
              playSuccessBeep();
              speak("Sample collection recorded.");
              queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              setProcessing(false);
              clearAndRestart();
            },
            onError: (err: any) => {
              showToastMessage("error", err?.message || err || "Failed to update sample collection time.");
              setProcessing(false);
              setShowCollectModal(false);
              clearAndRestart();
            },
          });
        },
        onError: (err: any) => {
          const errorData = err?.response?.data || err;
          const msg = typeof errorData === "string" ? errorData : errorData?.message || "Failed to record sample collection.";
          showToastMessage("error", msg);
          setProcessing(false);
          setShowCollectModal(false);
          clearAndRestart();
        },
      }
    );
  };

  const generateVacutainerBarcode = (subBarcode: string, session: any): string => {
    if (!subBarcode || subBarcode.length < 6) return subBarcode || "";
    const prefix = subBarcode.substring(0, 6);
    const period = session?.period_sort ?? session?.period_number ?? 1;
    const tpNum = Number(session?.timepoint_sort ?? session?.timepoint_number ?? 1);
    const timepoint = tpNum < 10 ? `0${tpNum}` : `${tpNum}`;
    return `${prefix}${period}${timepoint}1`;
  };

  const handleSkipSubject = () => {
    if (!subjectBarcode && subjectStatus !== "matched") {
      showToastMessage("error", "Please scan subject barcode before skipping.");
      return;
    }
    setSkipReason("");
    setShowSkipModal(true);
  };

  const handleConfirmSkipSubject = (reasonText: string) => {
    if (isProcessingRef.current) return;
    const targetVacutainer = vacutainerBarcode || generateVacutainerBarcode(subjectBarcode, sessionData);
    const formattedReason = reasonText.trim();
    const currentUser = GetUserData();

    setProcessing(true);

    const payload = {
      sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
      subjectbarcode: subjectBarcode,
      vaccutainerbarcode: targetVacutainer,
      result: "skipped",
      resultmessage: "Subject Sample Collection Skipped",
      skipreason: formattedReason,
      scanby: currentUser?.userid || 1,
      isverify: false,
      isinsert: true,
    };

    collectionService.InsertMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Subject skipped successfully.");
        speak("Subject Skipped.");
        playSuccessBeep();
        queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
        queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
        setShowSkipModal(false);
        setSkipReason("");
        setProcessing(false);
        clearAndRestart();
      },
      onError: (err: any) => {
        const errorData = err?.response?.data || err;
        const msg = typeof errorData === "string" ? errorData : errorData?.message || "Failed to skip subject.";
        showToastMessage("error", msg);
        speak("Failed to skip subject.");
        playErrorBeep();
        setProcessing(false);
      },
    });
  };

  // Live ticking clock for collect modal
  useEffect(() => {
    if (!showCollectModal) return;

    // Set initial display immediately
    setCollectTime(getNowDisplay());

    const intervalId = setInterval(() => {
      setCollectTime(getNowDisplay());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [showCollectModal]);

  const subjectCollections = useMemo(() => {
    return (sampleCollectionTimeQuery?.data?.data || []).filter((row: any) => !row.summarycard);
  }, [sampleCollectionTimeQuery?.data?.data]);

  const historyScans = useMemo(() => {
    if (activeTab === "step1") {
      const collectionScans = collectionService.SelectAllQuery?.data?.data || [];
      const mapped = collectionScans.map((scan: any) => {
        const matched = subjectCollections.find(
          (sc: any) => sc.barcode === scan.subjectbarcode
        );
        return {
          ...scan,
          dosingscheduletime: matched?.dosingscheduletime || null,
          samplecollectiontime: matched?.samplecollectiontime || null,
        };
      });
      return [...mapped].reverse();
    } else {
      const separationScans = separationService.SelectAllQuery?.data?.data || [];
      return [...separationScans];
    }
  }, [activeTab, collectionService.SelectAllQuery?.data?.data, separationService.SelectAllQuery?.data?.data, subjectCollections]);

  // Sync manual aliquot status list when total aliquots changes
  useEffect(() => {
    if (sessionData?.total_aliquots) {
      setAliquotBarcodes(Array(sessionData.total_aliquots).fill(""));
    }
  }, [sessionData?.total_aliquots]);

  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechTimeoutRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }
      if (activeUtteranceRef.current && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
      }
    };
  }, []);

  const isSpeakingRef = useRef(false);
  // Audio Speech Confirmation
  const speak = (msg: string, onEndCallback?: () => void) => {
    if (!("speechSynthesis" in window)) {
      onEndCallback?.();
      return;
    }
    const synth = window.speechSynthesis;

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    if (synth.speaking || synth.pending) {
      synth.cancel();
    }

    isSpeakingRef.current = true;

    // 150ms delay to allow browser speech synthesis engine to clear/reset queue
    speechTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      try {
        const utterance = new SpeechSynthesisUtterance(msg);
        utterance.rate = 1.0; // 1.0 is standard natural speech speed
        utterance.lang = "en-US";

        activeUtteranceRef.current = utterance;

        // Fallback safety timeout in case onend/onerror never fire (e.g. mobile Safari/Chrome background bugs)
        const safetyTimeout = setTimeout(() => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        }, 4000);

        const cleanUp = () => {
          clearTimeout(safetyTimeout);
          isSpeakingRef.current = false;
          activeUtteranceRef.current = null;
        };

        utterance.onend = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        utterance.onerror = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        synth.speak(utterance);
      } catch (err) {
        console.error("SpeechSynthesis error:", err);
        isSpeakingRef.current = false;
        if (isMountedRef.current) {
          onEndCallback?.();
        }
      }
    }, 150);
  };

  // Clear scans input state
  const clearAndRestart = () => {
    setSubjectBarcode("");
    setVacutainerBarcode("");
    setSubjectStatus("idle");
    setVacutainerStatus("idle");
    setIsCollectScanning(false);
    setActiveAliquotIndex(0);
    setAliquotStatus("idle");
    if (sessionData?.total_aliquots) {
      setAliquotBarcodes(Array(sessionData.total_aliquots).fill(""));
    }
    setFormMessage({ text: "", type: "" });
    scanLockRef.current = false;
    lastScannedRef.current = "";
    setProcessing(false);
    resetScanStartTime();
  };

  const handleCollectScanSubmit = (scannedVacutainer: string) => {
    const validation = validateStep1Barcodes(subjectBarcode, scannedVacutainer);
    if (validation !== "success" && scannedVacutainer.trim() !== vacutainerBarcode.trim()) {
      setFormMessage({ text: "Invalid or Mismatched Vacutainer Barcode", type: "error" });
      playErrorBeep();
      speak("Error. Invalid vacutainer barcode.");
      setTimeout(() => setFormMessage({ text: "", type: "" }), 2500);
      return;
    }

    setProcessing(true);

    // Insert FIRST, then update collection time
    collectionService.InsertMutation.mutate(
      {
        sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
        subjectbarcode: subjectBarcode,
        vaccutainerbarcode: scannedVacutainer,
        result: "valid",
        resultmessage: "Match & Sample Collected Successfully",
        isverify: false,
        isinsert: true,
      },
      {
        onSuccess: () => {
          const autoPayload = {
            vaccutainerbarcode: scannedVacutainer,
            samplecollectiontime: getNow(),
            updatedby: Number(user?.userid || 1),
            updatereason: "Auto sample collection on vacutainer scan",
          };

          CollectionTimeUpdateMutation.mutate(autoPayload, {
            onSuccess: (collRes: any) => {
              const successMsg = collRes?.message || "Sample collection recorded successfully!";
              setFormMessage({ text: successMsg, type: "success" });
              showToastMessage("success", successMsg);
              playSuccessBeep();
              speak("Sample collection recorded.");
              queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              setProcessing(false);
              setTimeout(() => {
                setIsCollectScanning(false);
                clearAndRestart();
              }, 1200);
            },
            onError: (err: any) => {
              const errMsg = typeof err === "string" ? err : err?.message || "Failed to update sample collection time.";
              setFormMessage({ text: errMsg, type: "error" });
              playErrorBeep();
              setProcessing(false);
              setTimeout(() => setFormMessage({ text: "", type: "" }), 2500);
            },
          });
        },
        onError: (err: any) => {
          const errorData = err?.response?.data || err;
          const errMsg = typeof errorData === "string" ? errorData : errorData?.message || "Failed to record scan";
          setFormMessage({ text: errMsg, type: "error" });
          playErrorBeep();
          setProcessing(false);
          setTimeout(() => setFormMessage({ text: "", type: "" }), 2500);
        },
      }
    );
  };

  // Validations helper
  const validateStep1Barcodes = (sub_barcode: string, vac_barcode: string) => {
    if (vac_barcode.length !== 10) return "vacutainer barcode length must be 10 characters";
    const sub_prefix = sub_barcode.substring(0, 6);
    const vac_prefix = vac_barcode.substring(0, 6);

    const period = parseInt(vac_barcode.substring(6, 7));
    const timepointBarcode = vac_barcode.substring(7, 9);
    const v_type = parseInt(vac_barcode.substring(9, 10));

    const timepointSession =
      sessionData?.timepoint_sort < 10
        ? `0${sessionData?.timepoint_sort}`
        : `${sessionData?.timepoint_sort}`;

    if (v_type !== 1) return "Not a vacutainer barcode";
    if (period !== sessionData?.period_sort) return "Invalid period";
    if (timepointBarcode !== timepointSession) return "Invalid timepoint";
    if (sub_prefix !== vac_prefix) return "Barcode Mismatch";

    return "success";
  };

  const validateStep2Barcode = (vac_barcode: string, ali_barcode: string, expected_tag: number) => {
    if (vac_barcode.length !== 10) return "Invalid vacutainer barcode";
    if (ali_barcode.length !== 10) return "Invalid aliquot barcode";

    const vac_type = parseInt(vac_barcode.substring(9, 10));
    const ali_type = parseInt(ali_barcode.substring(9, 10));
    const period = parseInt(ali_barcode.substring(6, 7));
    const timepointBarcode = ali_barcode.substring(7, 9);

    const vac_prefix = vac_barcode.substring(0, 9);
    const ali_prefix = ali_barcode.substring(0, 9);

    const timepointSession =
      sessionData?.timepoint_sort < 10
        ? `0${sessionData?.timepoint_sort}`
        : `${sessionData?.timepoint_sort}`;

    if (vac_type !== 1) return "Invalid vacutainer type";
    if (ali_type < 2) return "Not a valid aliquot barcode";
    if (period !== sessionData?.period_sort) return "Invalid period";
    if (timepointBarcode !== timepointSession) return "Invalid timepoint";
    if (ali_type !== expected_tag) return "Aliquot sequence mismatch";
    if (vac_prefix !== ali_prefix) return "Barcode Mismatch";

    return "success";
  };

  // Submit scan handler
  const handleBarcodeSubmit = (barcode: string) => {
    const value = barcode.trim();
    if (!value) return;

    // Centralised duplicate scanning lock to prevent double scan within 2.5s (essential for fast engines like BarcodeDetector API)
    if (scanLockRef.current || isSpeakingRef.current) return;
    if (isProcessingRef.current) return; // NEW — blocks until the actual network chain settles, however long it takes
    if (formMessage.type === "success" && !isCollectScanning) return;
    if (activeTab === "step1" && vacutainerStatus === "matched" && !isCollectScanning) return;
    if (
      collectionService.InsertMutation.isPending ||
      separationService.InsertMutation.isPending ||
      CollectionTimeUpdateMutation.isPending // NEW — was missing from this check
    ) return;
    if (lastScannedRef.current === value) return;

    scanLockRef.current = true;
    lastScannedRef.current = value;
    setTimeout(() => {
      scanLockRef.current = false;
      lastScannedRef.current = "";
    }, 2500); // 2.5s scan cooldown

    // logScanPerformance(value);
    setFormMessage({ text: "", type: "" });

    if (activeTab === "step1") {
      if (isCollectScanning) {
        handleCollectScanSubmit(value);
        return;
      }

      // Step 1: Subject -> Vacutainer
      if (subjectStatus === "idle") {
        // Scanning Subject Barcode
        if (value.length !== 10) {
          setSubjectStatus("mismatch");
          setFormMessage({ text: "Subject barcode length must be 10 characters", type: "error" });
          playErrorBeep();
          speak("Error. Subject barcode length is invalid.", () => {
            setSubjectBarcode("");
            setSubjectStatus("idle");
            setFormMessage({ text: "", type: "" });
          });
          return;
        }
        if (value.substring(6, 10) !== "0000") {
          setSubjectStatus("mismatch");
          setFormMessage({ text: "Invalid subject barcode", type: "error" });
          playErrorBeep();
          speak("Error. Invalid subject barcode", () => {
            setSubjectBarcode("");
            setSubjectStatus("idle");
            setFormMessage({ text: "", type: "" });
          });
          return;
        }

        setProcessing(true);
        collectionService.ValidateSubjectMutation.mutate(
          {
            sessionid: id ? parseInt(id) : sessionData?.sessionid,
            subjectbarcode: value,
          },
          {
            onSuccess: (res: any) => {
              setProcessing(false);
              const msg = res?.message || "Subject Scanned.";
              setSubjectBarcode(value);
              setSubjectStatus("matched");
              setVacutainerBarcode("");
              setVacutainerStatus("idle");
              setFormMessage({ text: msg, type: "success" });
              playSuccessBeep();
              speak(msg, () => {
                setFormMessage({ text: "", type: "" });
              });
              setTimeout(() => {
                setFormMessage({ text: "", type: "" });
              }, 1500);
            },
            onError: (err: any) => {
              setProcessing(false);
              const errorData = err?.response?.data || err;
              let backendMsg =
                typeof errorData === "string"
                  ? errorData
                  : errorData?.message ||
                  errorData?.resultmessage ||
                  errorData?.result ||
                  err?.message;

              const parsedMsg = Array.isArray(backendMsg)
                ? backendMsg.join(", ")
                : backendMsg;
              const displayMsg =
                typeof parsedMsg === "string" && parsedMsg
                  ? parsedMsg
                  : "Invalid subject barcode";

              setSubjectStatus("mismatch");
              setFormMessage({ text: displayMsg, type: "error" });
              playErrorBeep();
              speak(`Error. ${displayMsg}`, () => {
                setSubjectBarcode("");
                setSubjectStatus("idle");
                setFormMessage({ text: "", type: "" });
              });
            },
          }
        );
      } else {
        // Scanning Vacutainer Barcode - Pure client-side verification
        const validation = validateStep1Barcodes(subjectBarcode, value);
        if (validation === "success") {
          const msg = "Match Verified.";
          setFormMessage({ text: msg, type: "success" });
          setVacutainerBarcode(value);
          setVacutainerStatus("matched");
          playSuccessBeep();
          speak("Barcode Matched.");
        } else {
          setVacutainerStatus("mismatch");
          setFormMessage({ text: validation, type: "error" });
          playErrorBeep();
          if (validation === "Barcode Mismatch") {
            setProcessing(true);
            collectionService.InsertMutation.mutate({
              sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
              subjectbarcode: subjectBarcode,
              vaccutainerbarcode: value,
              result: "invalid",
              resultmessage: "mismatched barcode",
            }, {
              onSuccess: () => {
                setProcessing(false);
                queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-collection-select-all"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              },
              onError: () => {
                setProcessing(false);
              }
            });
          }
          speak(`Error. ${validation}`);
          setTimeout(() => {
            setVacutainerBarcode("");
            setVacutainerStatus("idle");
            setFormMessage({ text: "", type: "" });
          }, 2500);
        }
      }
    } else {
      // Step 2: Vacutainer -> Aliquots
      if (vacutainerStatus === "idle") {
        // Scanning Vacutainer Barcode
        if (value.length !== 10) {
          setFormMessage({ text: "Invalid vacutainer barcode length", type: "error" });
          playErrorBeep();
          speak("Error. Invalid vacutainer barcode length.");
          setTimeout(() => {
            setVacutainerBarcode("");
            setFormMessage({ text: "", type: "" });
          }, 2500);
          return;
        }
        const vac_type = parseInt(value.substring(9, 10));
        const vac_period = parseInt(value.substring(6, 7));
        const vac_timepoint = value.substring(7, 9);
        const timepointSession =
          sessionData?.timepoint_sort < 10
            ? `0${sessionData?.timepoint_sort}`
            : `${sessionData?.timepoint_sort}`;

        if (vac_period !== sessionData?.period_sort || vac_timepoint !== timepointSession || vac_type !== 1) {
          let errorMsg = "Invalid vacutainer barcode parameter.";
          if (vac_type !== 1) errorMsg = "Invalid vacutainer type.";
          else if (vac_period !== sessionData?.period_sort) errorMsg = "Invalid period.";
          else if (vac_timepoint !== timepointSession) errorMsg = "Invalid timepoint.";
          setFormMessage({ text: errorMsg, type: "error" });
          playErrorBeep();
          speak(`Error. ${errorMsg}`);
          setTimeout(() => {
            setVacutainerBarcode("");
            setFormMessage({ text: "", type: "" });
          }, 2500);
          return;
        }

        setProcessing(true);
        separationService.ValidateVaccutainerMutation.mutate(
          {
            sessionid: id ? parseInt(id) : sessionData?.sessionid,
            vaccutainerbarcode: value,
          },
          {
            onSuccess: (res: any) => {
              setProcessing(false);
              const msg = res?.message || "Vacutainer Scanned.";
              setVacutainerBarcode(value);
              setVacutainerStatus("matched");
              setActiveAliquotIndex(1);
              setFormMessage({ text: msg, type: "success" });
              playSuccessBeep();
              speak(msg, () => {
                setFormMessage({ text: "", type: "" });
              });
              setTimeout(() => {
                setFormMessage({ text: "", type: "" });
              }, 1500);
            },
            onError: (err: any) => {
              setProcessing(false);
              const errorData = err?.response?.data || err;
              let backendMsg =
                typeof errorData === "string"
                  ? errorData
                  : errorData?.message ||
                  errorData?.resultmessage ||
                  errorData?.result ||
                  err?.message;

              const parsedMsg = Array.isArray(backendMsg)
                ? backendMsg.join(", ")
                : backendMsg;
              const displayMsg =
                typeof parsedMsg === "string" && parsedMsg
                  ? parsedMsg
                  : "Invalid vacutainer barcode";

              setVacutainerStatus("mismatch");
              setFormMessage({ text: displayMsg, type: "error" });
              playErrorBeep();
              speak(`Error. ${displayMsg}`, () => {
                setVacutainerBarcode("");
                setVacutainerStatus("idle");
                setFormMessage({ text: "", type: "" });
              });
            },
          }
        );
      } else {
        // Scanning Aliquot barcode at activeAliquotIndex
        const expected_tag = activeAliquotIndex + 1;
        const validation = validateStep2Barcode(vacutainerBarcode, value, expected_tag);

        if (validation === "success") {
          const updated = [...aliquotBarcodes];
          updated[activeAliquotIndex - 1] = value;
          setAliquotBarcodes(updated);
          playSuccessBeep();

          const isLast = activeAliquotIndex === (sessionData?.total_aliquots || 2);

          setProcessing(true);
          separationService.InsertMutation.mutate(
            {
              sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
              vaccutainerbarcode: vacutainerBarcode,
              aliquotbarcode: value,
              result: "valid",
              resultmessage: "Matched Successfully",
            },
            {
              onSuccess: (res: any) => {
                setProcessing(false);
                queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-seperation-select-all"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });

                const msg = res?.message || (isLast ? "Match Verified." : `Aliquot ${activeAliquotIndex} matched.`);
                setFormMessage({ text: msg, type: "success" });

                if (isLast) {
                  speak("Barcode Matched.");
                  setTimeout(() => {
                    clearAndRestart();
                  }, 1200);
                } else {
                  const nextIdx = activeAliquotIndex + 1;
                  setActiveAliquotIndex(nextIdx);
                  speak("Barcode Matched.");
                  setTimeout(() => {
                    setFormMessage({ text: "", type: "" });
                  }, 800);
                }
              },
              onError: (err: any) => {
                setProcessing(false);
                const errorData = err?.response?.data || err;
                const displayMsg = typeof errorData === "string" ? errorData : errorData?.message || err?.message || "Failed to record scan";
                setFormMessage({ text: displayMsg, type: "error" });
                playErrorBeep();
                speak(`Error. ${displayMsg}`);
                setTimeout(() => {
                  setAliquotStatus("idle");
                  setFormMessage({ text: "", type: "" });
                }, 2500);
              },
            },
          );
        } else {
          setAliquotStatus("mismatch");
          setFormMessage({ text: validation, type: "error" });
          playErrorBeep();
          if (validation === "Barcode Mismatch") {
            setProcessing(true);
            separationService.InsertMutation.mutate({
              sessionid: id ? parseInt(id) : sessionData?.sessionid || 1,
              vaccutainerbarcode: vacutainerBarcode,
              aliquotbarcode: value,
              result: "invalid",
              resultmessage: "mismatched barcode",
            }, {
              onSuccess: () => {
                setProcessing(false);
                queryClient.invalidateQueries({ queryKey: ["scanning-scan-sample-seperation-select-all"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
                queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
              },
              onError: () => {
                setProcessing(false);
              }
            });
          }
          speak(`Error. ${validation}`);
          setTimeout(() => {
            setAliquotStatus("idle");
            setFormMessage({ text: "", type: "" });
          }, 2500);
        }
      }
    }
  };

  const handleBarcodeSubmitRef = useRef<any>(null);
  useEffect(() => {
    handleBarcodeSubmitRef.current = handleBarcodeSubmit;
  });

  const [torchOn, setTorchOn] = useState(false);

  const [isResettingScanner, setIsResettingScanner] = useState(false);

  useEffect(() => {
    setIsResettingScanner(true);
    const timer = setTimeout(() => setIsResettingScanner(false), 50);
    return () => clearTimeout(timer);
  }, [subjectStatus, vacutainerStatus, activeAliquotIndex, activeTab, isCollectScanning]);

  const customStreamRef = useRef<MediaStream | null>(null);
  const detectionIntervalRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Helper utility to load external script asynchronously
  const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script ${src}`));
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    let active = true;

    const cleanupActiveScanners = async (stopStream = true) => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
      if (stopStream && customStreamRef.current) {
        try {
          customStreamRef.current.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping custom stream tracks", e);
        }
        customStreamRef.current = null;
      }
      if (videoRef.current && stopStream) {
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject = null;
        }
      }
    };

    const initEngine = async () => {
      if (!activeTab || activeTab === "select" || isShowingAllScans || isResettingScanner) {
        await cleanupActiveScanners(true);
        return;
      }

      if (activeTab === "step1" && vacutainerStatus === "matched" && !isCollectScanning) {
        await cleanupActiveScanners(true);
        return;
      }

      await cleanupActiveScanners(false);

      if (!videoRef.current) return;

      const videoElement = videoRef.current;

      // Check for secure context or getUserMedia availability
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        if (!isSecure) {
          showToastMessage("error", "Camera blocked: Mobile devices require a secure HTTPS connection or localhost to access the camera.");
        } else {
          showToastMessage("error", "Camera API is not supported in this browser environment.");
        }
        return;
      }

      resetScanStartTime();

      try {
        if (!("BarcodeDetector" in window)) {
          await loadScript("https://cdn.jsdelivr.net/npm/barcode-detector@3/dist/iife/index.min.js");
          if (!active) return;
        }

        const BarcodeDetectorClass = (window as any).BarcodeDetector;
        if (!BarcodeDetectorClass) {
          throw new Error("BarcodeDetector API polyfill failed to load");
        }

        let detectorFormats = ["code_128", "code_39", "itf", "qr"];
        if (typeof BarcodeDetectorClass.getSupportedFormats === "function") {
          try {
            const supported = await BarcodeDetectorClass.getSupportedFormats();
            detectorFormats = detectorFormats.filter((f) => supported.includes(f));
          } catch (e) {
            console.error("Failed to fetch supported formats:", e);
          }
        }

        const detector = new BarcodeDetectorClass({
          formats: detectorFormats.length > 0 ? detectorFormats : ["qr"],
        });

        let stream = customStreamRef.current;
        const isStreamLive = stream && stream.getVideoTracks().some((t) => t.readyState === "live");

        if (!isStreamLive) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" } },
            audio: false,
          });

          if (!active) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          customStreamRef.current = stream;
        }

        if (videoElement.srcObject !== stream) {
          videoElement.srcObject = stream;
        }
        videoElement.setAttribute("playsinline", "true");
        await videoElement.play().catch(() => { });

        let isDetecting = false;
        const detectFrame = async () => {
          if (!active || isDetecting || isProcessingRef.current) return; // NEW check added
          isDetecting = true;
          try {
            if (videoElement && videoElement.readyState >= 2) {
              const barcodes = await detector.detect(videoElement);
              if (barcodes && barcodes.length > 0 && active) {
                const code = barcodes[0].rawValue;
                if (code) {
                  handleBarcodeSubmitRef.current?.(code);
                }
              }
            }
          } catch (e) {
            // frame not ready or video stopped
          } finally {
            isDetecting = false;
          }
        };

        detectionIntervalRef.current = setInterval(detectFrame, 150);
      } catch (err: any) {
        console.error("Failed to initialize Barcode Detector:", err);
        const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        if (err && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")) {
          showToastMessage("error", "Camera permission denied. Please reset permissions in your browser address bar/settings.");
        } else if (!isSecure) {
          showToastMessage("error", "Camera blocked: Mobile devices require a secure HTTPS connection or localhost to access the camera.");
        } else {
          showToastMessage("error", `Failed to start camera for barcode detector: ${err.message || err}`);
        }
      }
    };

    initEngine();

    return () => {
      active = false;
      cleanupActiveScanners(true);
    };
  }, [activeTab, isShowingAllScans, isResettingScanner, subjectStatus, vacutainerStatus, isCollectScanning, activeAliquotIndex]);

  const toggleTorch = async () => {
    try {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject as MediaStream | null;
        const track = stream?.getVideoTracks()?.[0];
        if (track) {
          const capabilities = track.getCapabilities() as any;
          if (capabilities.torch) {
            const nextTorchState = !torchOn;
            await track.applyConstraints({
              advanced: [{ torch: nextTorchState } as any],
            });
            setTorchOn(nextTorchState);
          } else {
            showToastMessage("error", "Torch is not supported on this device/camera.");
          }
        }
      }
    } catch (err) {
      console.error("Failed to toggle torch", err);
    }
  };


  // Helper stats for circular ring & segmented bar progress
  const getProgressCircleValues = () => {
    if (!sessionData) return { pct: 0, text: "0/0", label: "0%" };
    if (activeTab === "step1") {
      const completed = completedSubs;
      const total = totalSubs;
      const pct = total > 0 ? (completed / total) * 100 : 0;
      return {
        pct,
        text: `${completed}/${total}`,
        label: `${Math.round(pct)}%`,
        completed,
        total,
      };
    } else {
      const completed = sessionData.completed_aliquots || 0;
      const total = (sessionData?.total_aliquots ?? 0) * (sessionData?.totalsubjects ?? sessionData?.total_subjects ?? 0);

      const pct = total > 0 ? (completed / total) * 100 : 0;
      return {
        pct,
        text: `${completed}/${total}`,
        label: `${Math.round(pct)}%`,
        completed,
        total,
      };
    }
  };

  const stats = getProgressCircleValues();
  const strokeDashoffset = 125.6 - (125.6 * Math.min(stats.pct, 100)) / 100;

  // ───────────────────────────────────────────────────────────────────────────
  // 🖥️ Layout Branch Rendering
  // ───────────────────────────────────────────────────────────────────────────
  if (SelectQuery.isPending) {
    return <FullPageLoader />;
  }

  // A. Desktop Layout (> 1024px)
  if (isDesktop) {
    return (
      <React.Fragment>
        <div className={`page-content ${ac.pageContent}`}>
          {UpdateMutation.isPending && <FullPageLoader />}
          <Container fluid className="pt-0">
            {/* ── Header ── */}
            <div className={ac.pageHeader}>
              <div className="d-flex align-items-center gap-3">
                <Button
                  color="light"
                  outline
                  className="px-2 py-1 bg-white border"
                  onClick={() => handleUpdateStatus("paused", true)}
                  disabled={UpdateMutation.isPending}
                >
                  <i className="ri-arrow-left-line text-dark"></i>
                </Button>
                <div>
                  <h5 className={ac.sessionTitle}>
                    Active Session
                    <span
                      className={`badge bg-light text-secondary border fw-normal ms-2 ${ac.sessionBadge}`}
                    >
                      {sessionData?.sessionno ||
                        sessionData?.sessionid ||
                        "SESS-0091"}
                    </span>
                  </h5>
                  <p className={ac.sessionSubtitle}>
                    Execute and monitor the accessioning workflow for the current
                    study period.
                  </p>
                </div>
              </div>
              <div className="d-flex gap-3 align-items-center">
                {sessionData?.display_status === "PAUSED" ? (
                  <CommonButton
                    module={PERMISSION_MODULES.SESSION}
                    action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                    color="success"
                    outline
                    onClick={() => handleUpdateStatus("active", false)}
                    disabled={UpdateMutation.isPending}
                  >
                    Resume Session
                  </CommonButton>
                ) : (
                  <CommonButton
                    module={PERMISSION_MODULES.SESSION}
                    action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                    color="secondary"
                    outline
                    onClick={() => handleUpdateStatus("paused", true)}
                    disabled={UpdateMutation.isPending}
                  >
                    Pause Session
                  </CommonButton>
                )}
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                  color="primary"
                  outline
                  onClick={() => handleUpdateStatus("completed", true)}
                  disabled={
                    UpdateMutation.isPending ||
                    completedSubs !== totalSubs
                  }
                >
                  Complete Session
                </CommonButton>
              </div>
            </div>

            {/* ── White Panel ── */}
            <div className={`${styles.listPanel} mb-3`}>
              {/* Study Metadata */}
              <div className="px-4 pt-3 pb-2 border-bottom">
                <div className="d-flex justify-content-between align-items-end">
                  <div
                    className={`d-flex align-items-center gap-4 ${ac.metaRow}`}
                  >
                    <div>
                      <i className="ri-folder-line me-1 text-primary"></i> Study:
                      <span className="fw-bold text-dark ms-1">
                        {sessionData?.studycode}
                      </span>
                    </div>
                    <div>
                      <i className="ri-calendar-line me-1 text-primary"></i>{" "}
                      Period:
                      <span className="fw-bold text-dark ms-1">
                        {sessionData?.period_number}
                      </span>
                    </div>
                    <div>
                      <i className="ri-time-line me-1 text-primary"></i> Time
                      Point:{" "}
                      <span className="fw-bold text-dark ms-1">
                        {sessionData?.timepoint_number}
                      </span>
                    </div>
                    <div>
                      <i className="ri-building-2-line me-1 text-primary"></i> Dosing Station:{" "}
                      <span className="fw-bold text-dark ms-1">
                        {sessionData?.dosingstationno ? `DS-${String(sessionData.dosingstationno).padStart(2, "0")}` : "DS-01"}
                      </span>
                    </div>
                  </div>
                  <div className={ac.progressWrapper} style={{ width: "260px" }}>
                    <div
                      className={`d-flex justify-content-between text-muted mb-1 ${ac.progressLabel}`}
                    >
                      <span>Session Progress</span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold text-success" title="Completed">
                          {completedSubs}
                        </span>
                        <span className="text-muted">/</span>
                        <span className="fw-bold text-info" title="In Progress">
                          {inProgressSubs}
                        </span>
                        <span className="text-muted">/</span>
                        <span className="fw-bold text-dark" title="Total Subjects">
                          {totalSubs}
                        </span>
                        <i
                          className="ri-eye-line text-primary fs-14 ms-1"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowProgressModal(true)}
                          title="View Detailed Progress"
                        />
                      </div>
                    </div>
                    <Progress multi className={ac.progressBar}>
                      <Progress bar value={(completedSubs / (totalSubs || 1)) * 100} color="success" />
                      <Progress
                        bar
                        value={(inProgressSubs / (totalSubs || 1)) * 100}
                        color="info"
                      />
                    </Progress>
                  </div>
                </div>
              </div>

              {/* ── Tabs ── */}
              <div className={tabStyles.tabContainer}>
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}
                  type="button"
                  className={`${tabStyles.tabBtn} ${activeTab === "step1" ? tabStyles.active : ""}`}
                  onClick={() => toggleTab("step1")}
                >
                  Step 1: Subject to Vacutainer
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}
                  type="button"
                  className={`${tabStyles.tabBtn} ${activeTab === "step2" ? tabStyles.active : ""}`}
                  onClick={() => toggleTab("step2")}
                >
                  Step 2: Vacutainer to Aliquots
                </CommonButton>
              </div>

              {/* ── Content ── */}
              <div>
                <Routes>
                  <Route
                    index
                    element={
                      <SubjectToVacutainer
                        sessionData={sessionData}
                        sessionid={id}
                        isParentModalOpen={showProgressModal}
                      />
                    }
                  />
                  <Route
                    path="step2"
                    element={
                      <RouteGuard
                        module={PERMISSION_MODULES.SESSION}
                        action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}>
                        <VacutainerToAliquots
                          sessionData={sessionData}
                          sessionid={id}
                          isParentModalOpen={showProgressModal}
                        />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <RouteGuard
                        module={PERMISSION_MODULES.SESSION}
                        action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}>
                        <SubjectToVacutainer
                          sessionData={sessionData}
                          sessionid={id}
                          isParentModalOpen={showProgressModal}
                        />
                      </RouteGuard>
                    }
                  />
                </Routes>
              </div>
            </div>
          </Container>
        </div>

        <ScanningSessionProgressModal
          isOpen={showProgressModal}
          toggle={() => setShowProgressModal(!showProgressModal)}
          sessionData={sessionData}
        />
      </React.Fragment>
    );
  }





  return (
    <React.Fragment>
      <div className={resStyles.pageContent}>
        {UpdateMutation.isPending && <FullPageLoader />}

        <div className={resStyles.mobileContainer}>
          {isShowingAllScans ? (
            <MobileScansOverlay
              historyScans={historyScans}
              activeTab={activeTab}
              setIsShowingAllScans={setIsShowingAllScans}
            />
          ) : (
            <>
              <MobileHeader
                sessionData={sessionData}
                activeTab={activeTab}
                toggleTab={toggleTab}
                handleUpdateStatus={handleUpdateStatus}
                setShowProgressModal={setShowProgressModal}
                completedSubsCount={completedSubs}
                inProgressSubsCount={inProgressSubs}
              />

              {activeTab === "select" ? (
                <MobileWorkflowSelector
                  sessionData={sessionData}
                  toggleTab={toggleTab}
                  handleUpdateStatus={handleUpdateStatus}
                  step1CompletedSubs={completedSubs + inProgressSubs}
                  fullyCompletedSubs={completedSubs}
                  totalSubs={totalSubs}
                />
              ) : (
                <>

                  <div className={resStyles.cardsContainer}>
                    {activeTab === "step1" ? (
                      <RouteGuard
                        module={PERMISSION_MODULES.SESSION}
                        action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}>
                        <MobileStep1Scanner
                          videoRef={videoRef}
                          torchOn={torchOn}
                          toggleTorch={toggleTorch}
                          subjectBarcode={subjectBarcode}
                          subjectStatus={subjectStatus}
                          vacutainerBarcode={vacutainerBarcode}
                          vacutainerStatus={vacutainerStatus}
                          handleBarcodeSubmit={handleBarcodeSubmit}
                          clearAndRestart={clearAndRestart}
                          formMessage={formMessage}
                          completedSubs={completedSubs + inProgressSubs}
                          totalSubs={totalSubs}
                          skippedSubs={sessionData?.skipped_subjects || 0}
                          onCollectClick={handleCollectClick}
                          isCollectScanning={isCollectScanning}
                          onSkipClick={handleSkipSubject}
                          isSkipEnabled={subjectStatus === "matched" || Boolean(subjectBarcode)}
                          sessionData={sessionData}
                          isProcessing={isProcessing}
                        />
                      </RouteGuard>
                    ) : (
                      <RouteGuard
                        module={PERMISSION_MODULES.SESSION}
                        action={PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}>
                        <MobileStep2Scanner
                          videoRef={videoRef}
                          torchOn={torchOn}
                          toggleTorch={toggleTorch}
                          vacutainerBarcode={vacutainerBarcode}
                          vacutainerStatus={vacutainerStatus}
                          activeAliquotIndex={activeAliquotIndex}
                          totalAliquots={sessionData?.total_aliquots || 2}
                          aliquotBarcodes={aliquotBarcodes}
                          handleBarcodeSubmit={handleBarcodeSubmit}
                          clearAndRestart={clearAndRestart}
                          formMessage={formMessage}
                          completedSubs={completedSubs}
                          totalSubs={totalSubs}
                          sessionData={sessionData}
                          isProcessing={isProcessing}
                        />
                      </RouteGuard>
                    )}
                  </div>



                  {/* 📋 Mini Recent Scan Logs (Last 3) */}
                  <div className={resStyles.recentScansMiniList}>
                    <div className={resStyles.miniListHeader}>
                      <h6>Recent Scans</h6>
                      <button type="button" onClick={() => setIsShowingAllScans(true)}>
                        View All
                      </button>
                    </div>
                    <div className={resStyles.miniListItems}>
                      {historyScans.slice(0, 3).map((row: any, idx: number) => {
                        const resultStatus = (row.result || "valid").toLowerCase();

                        return (
                          <div key={idx} className={resStyles.miniListItem}>
                            <div className={resStyles.miniItemLeft} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span className={resStyles.miniItemTime} style={{ minWidth: "60px", display: "inline-block" }}>
                                  {row.scandate ? new Date(row.scandate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}
                                </span>
                                <span className={resStyles.miniItemValues}>
                                  {activeTab === "step1" ? (
                                    `${row.subjectbarcode || "-"} → ${row.vaccutainerbarcode || "-"}`
                                  ) : (
                                    `${row.vaccutainerbarcode || "-"} → ${row.aliquotbarcode || "-"}`
                                  )}
                                </span>
                              </div>
                              {activeTab === "step1" && (row.dosingscheduletime || row.samplecollectiontime) && (
                                <span style={{ fontSize: "11px", color: "#64748b", paddingLeft: "70px", fontWeight: "normal" }}>
                                  (Sch: {row.dosingscheduletime ? row.dosingscheduletime.substring(0, 5) : "-"} | Coll: {row.samplecollectiontime ? row.samplecollectiontime.substring(0, 5) : "-"})
                                </span>
                              )}
                            </div>
                            {resultStatus === "skipped" ? (
                              <span
                                className="badge bg-warning bg-opacity-10 border border-warning border-opacity-50 px-2 py-1 text-uppercase fw-bold shadow-sm"
                                style={{
                                  fontSize: "11px",
                                  backgroundColor: "#fff8e6",
                                  color: "#b45309",
                                  borderColor: "#f59e0b",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                }}
                              >
                                SKIPPED
                              </span>
                            ) : resultStatus === "valid" ? (
                              <span
                                className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1 text-uppercase fw-bold d-inline-flex align-items-center gap-1"
                                style={{
                                  fontSize: "11px",
                                  backgroundColor: "#dcfce7",
                                  color: "#15803d",
                                  borderColor: "#86efac",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                }}
                              >
                                VALID
                              </span>
                            ) : (
                              <span
                                className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-2 py-1 text-uppercase fw-bold d-inline-flex align-items-center gap-1"
                                style={{
                                  fontSize: "11px",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                }}
                              >
                                {row.result || "INVALID"}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {historyScans.length === 0 && (
                        <div className={resStyles.miniListEmpty}>No scans recorded yet.</div>
                      )}
                    </div>
                  </div>
                  {/* Bottom Action Stack */}
                  <div className={resStyles.bottomActionsStack}>
                    <button
                      type="button"
                      className={resStyles.bottomActionBtn}
                      onClick={() => handleUpdateStatus("paused", true)}
                    >
                      <i className="ri-pause-line"></i> Pause Session
                    </button>
                    <button
                      type="button"
                      className={resStyles.bottomActionBtn}
                      onClick={() => setIsShowingAllScans(true)}
                    >
                      <i className="ri-eye-line"></i> View All Scans
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <ScanningSessionProgressModal
        isOpen={showProgressModal}
        toggle={() => setShowProgressModal(!showProgressModal)}
        sessionData={sessionData}
      />

      <Modal isOpen={showCollectModal} toggle={() => setShowCollectModal(false)} centered className="common_modal" size="sm">
        <ModalHeader toggle={() => setShowCollectModal(false)} className="fw-bold border-bottom py-2 px-3">
          <i className="ri-drop-line me-2 text-primary fs-18 align-middle" />
          <span className="align-middle fs-16">Confirm Collection</span>
        </ModalHeader>
        <ModalBody className="py-3 px-3 text-center">
          <div className="mb-2">
            <div
              style={{
                backgroundColor: "#e6fffa",
                color: "#0d9488",
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              <i className="ri-checkbox-circle-fill"></i>
            </div>
          </div>
          <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "14px" }}>
            Record Collection Time
          </h6>
          <p className="text-muted mb-3" style={{ fontSize: "12px", lineHeight: "1.4" }}>
            Confirm sample collection for Subject <strong>{subjectBarcode}</strong>?
          </p>
          <div
            className="d-flex align-items-center justify-content-center gap-2 py-1 px-3 rounded mb-1"
            style={{ backgroundColor: "#f8fafc", border: "1px dashed #cbd5e1", display: "inline-flex", minWidth: "150px" }}
          >
            <i className="ri-time-line text-secondary fs-16" />
            <span className="fw-bold text-dark fs-14">{collectTime}</span>
          </div>
        </ModalBody>
        <ModalFooter className="bg-white border-top py-2 px-3 d-flex justify-content-end gap-2">
          <Button size="sm" color="dark" outline onClick={() => setShowCollectModal(false)} disabled={collectionService.InsertMutation.isPending || CollectionTimeUpdateMutation.isPending}>
            Cancel
          </Button>
          <Button size="sm" color="teal" style={{ backgroundColor: "#0d9488", borderColor: "#0d9488", color: "#fff" }} onClick={handleConfirmCollect} disabled={collectionService.InsertMutation.isPending || CollectionTimeUpdateMutation.isPending}>
            {(collectionService.InsertMutation.isPending || CollectionTimeUpdateMutation.isPending) ? "Saving..." : "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── SKIP REASON MODAL ── */}
      <SkipReasonModal
        isOpen={showSkipModal}
        toggle={() => setShowSkipModal(false)}
        subjectBarcode={subjectBarcode}
        onSubmit={handleConfirmSkipSubject}
        isPending={collectionService.InsertMutation.isPending}
      />
    </React.Fragment>
  );
};

export default ScanningSessionPage;
